// Seat preference — stored as a per-passenger field that travels with
// the rest of the passenger object through every surface (booking JSONB
// blob, relational `passengers` row, saved-passenger profile, admin
// edit modal, flight details modal, etc.).
//
// We intentionally model this as a UI-level preference rather than a
// hard seat assignment. The concierge team uses these values to pick
// real seats together as a group when fares allow it; if not, the
// preferences guide the upgrade conversation with the customer.

export const SEAT_PREFERENCE_OPTIONS = [
  { value: 'window', label: 'Window' },
  { value: 'middle', label: 'Middle' },
  { value: 'aisle', label: 'Aisle' },
  { value: 'no_preference', label: 'No preference' },
];

// Default cycle assumes a 3-3 cabin: rows of 6 alternate
// W-M-A | A-M-W so groups travelling together naturally cluster across
// the aisle. Pattern repeats every 6 passengers, which the user
// described as "the next row".
export const DEFAULT_SEAT_PREFERENCE_PATTERN = [
  'window',
  'middle',
  'aisle',
  'aisle',
  'middle',
  'window',
];

/**
 * Returns the default seat preference for the Nth passenger (0-indexed).
 * Wraps every 6, so passenger 7 starts the pattern over at "window".
 */
export function getDefaultSeatPreference(index) {
  if (typeof index !== 'number' || index < 0) return DEFAULT_SEAT_PREFERENCE_PATTERN[0];
  return DEFAULT_SEAT_PREFERENCE_PATTERN[index % DEFAULT_SEAT_PREFERENCE_PATTERN.length];
}

/**
 * Maps any seat preference value (camelCase or snake_case key, label,
 * synonym, or empty) to a canonical option value or empty string.
 * Used by display sites that may receive un-normalized input.
 */
export function normalizeSeatPreference(value) {
  if (!value) return '';
  const v = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (v === 'window' || v === 'middle' || v === 'aisle' || v === 'no_preference') return v;
  if (v === 'none' || v === 'any') return 'no_preference';
  return '';
}

/** Pretty label for a stored preference value (or empty string). */
export function getSeatPreferenceLabel(value) {
  const normalized = normalizeSeatPreference(value);
  const found = SEAT_PREFERENCE_OPTIONS.find((opt) => opt.value === normalized);
  return found ? found.label : '';
}
